import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/sessions';

const C = {
  bg:'#0D1B2A', surface:'#112233', surface2:'#162840',
  border:'rgba(255,255,255,.08)', border2:'rgba(255,255,255,.12)',
  text:'#F0F6FF', text2:'#A8BFD0', text3:'#607080',
  gold:'#C9A84C', goldLight:'#F5EDD4',
  accent:'#3AAF58', accentLight:'rgba(58,175,88,.15)',
  red:'#EF4444', redBg:'rgba(239,68,68,.12)',
  amber:'#F59E0B', amberBg:'rgba(245,158,11,.12)',
  blue:'#3B82F6', blueBg:'rgba(59,130,246,.12)',
  green:'#10B981', greenBg:'rgba(16,185,129,.12)',
  primary:'#153D4B',
};

const inr = n => '₹' + Number(Math.round(n)||0).toLocaleString('en-IN');

const PRIORITY_CONFIG = {
  High:   { color:'#EF4444', bg:'rgba(239,68,68,.15)', icon:'🔴' },
  Medium: { color:'#F59E0B', bg:'rgba(245,158,11,.15)', icon:'🟠' },
  Low:    { color:'#10B981', bg:'rgba(16,185,129,.15)', icon:'🟢' },
};

const CATEGORY_ICONS = {
  Pricing:'💰', Occupancy:'🏨', Upsell:'⬆️', Channel:'📡', Package:'📦',
};

export default function RevenueAI() {
  const [context, setContext] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/ai/revenue-context`);
      const d = await r.json();
      setContext(d);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { loadContext(); }, [loadContext]);

  const analyze = async () => {
    if (!context) return;
    setAnalyzing(true);
    try {
      const r = await fetch(`${API}/ai/revenue-recommendations`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ context })
      });
      const d = await r.json();
      if (r.ok) { setRecommendations(d); setActiveTab('recommendations'); }
    } catch(e) {}
    setAnalyzing(false);
  };

  const askRevenue = async () => {
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages(p=>[...p,{role:'user',content:userMsg}]);
    setChatLoading(true);
    try {
      const r = await fetch(`${API}/ai/chat`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          system: `You are Botanix Revenue AI. You specialize in room pricing, occupancy optimization, revenue management and forecasting for a premium nature resort in Gurgaon. Always give specific numbers and actionable advice. Current data: ${JSON.stringify(context)}`,
          messages: [...chatMessages.slice(-6), {role:'user',content:userMsg}],
        })
      });
      const d = await r.json();
      setChatMessages(p=>[...p,{role:'assistant',content:d.content?.[0]?.text||'No response'}]);
    } catch(e) { setChatMessages(p=>[...p,{role:'assistant',content:'Error connecting to Revenue AI'}]); }
    setChatLoading(false);
  };

  const healthColor = score => score>=75?C.green:score>=50?C.amber:C.red;

  if (loading) return <div style={{background:C.bg,minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:C.text3}}>Loading Revenue AI...</div>;

  const totalRooms = (context?.occupancy_by_type||[]).reduce((s,r)=>s+parseInt(r.count||0),0);
  const occupiedRooms = (context?.occupancy_by_type||[]).filter(r=>r.status==='Occupied').reduce((s,r)=>s+parseInt(r.count||0),0);
  const occupancyPct = totalRooms>0?Math.round(occupiedRooms/totalRooms*100):0;

  return (
    <div style={{background:C.bg,minHeight:'100vh',color:C.text,padding:'24px 28px',fontFamily:'system-ui,sans-serif'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.text}}>💰 Revenue Intelligence AI</div>
          <div style={{fontSize:13,color:C.text3,marginTop:2}}>Pricing optimization · Occupancy forecasting · Revenue recommendations</div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={loadContext} style={{padding:'9px 14px',background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,color:C.text2,fontSize:13,cursor:'pointer'}}>↻ Refresh</button>
          <button onClick={analyze} disabled={analyzing}
            style={{padding:'9px 20px',background:analyzing?C.surface2:`linear-gradient(135deg,${C.gold},#E8C97A)`,
              color:analyzing?C.text3:'#1A1A1A',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:analyzing?'default':'pointer'}}>
            {analyzing?'⏳ Analyzing...':'🧠 Run Revenue Analysis'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:6,marginBottom:20}}>
        {[['overview','📊 Overview'],['recommendations','🎯 Recommendations'],['rates','💰 Rate Advisor'],['forecast','📈 Forecast'],['chat','💬 Ask Revenue AI']].map(([id,label])=>(
          <button key={id} onClick={()=>setActiveTab(id)}
            style={{padding:'8px 16px',background:activeTab===id?C.gold:'transparent',color:activeTab===id?'#1A1A1A':C.text2,
              border:`1px solid ${activeTab===id?C.gold:C.border}`,borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer'}}>
            {label} {id==='recommendations'&&recommendations?`(${recommendations.recommendations?.length||0})`:''}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab==='overview' && context && (
        <div>
          {/* KPIs */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
            {[
              {label:'Current Occupancy',value:`${occupancyPct}%`,color:occupancyPct>70?C.green:occupancyPct>40?C.amber:C.red,icon:'🏨'},
              {label:'Total Rooms',value:totalRooms,color:C.blue,icon:'🛏️'},
              {label:'Room Types',value:context.room_types?.length||0,color:C.gold,icon:'🏷️'},
              {label:'Active Pricing Rules',value:context.pricing_rules?.length||0,color:C.accent,icon:'📋'},
            ].map((k,i)=>(
              <div key={i} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 18px',borderTop:`3px solid ${k.color}`}}>
                <div style={{fontSize:24,marginBottom:8}}>{k.icon}</div>
                <div style={{fontSize:26,fontWeight:900,color:k.color}}>{k.value}</div>
                <div style={{fontSize:11,color:C.text3,textTransform:'uppercase',marginTop:4}}>{k.label}</div>
              </div>
            ))}
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
            {/* Room Types & Rates */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:14}}>🏷️ Room Types & Base Rates</div>
              {(context.room_types||[]).map((rt,i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.surface2,borderRadius:8,marginBottom:8}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{rt.name}</div>
                    <div style={{fontSize:11,color:C.text3}}>{rt.room_count} rooms · {rt.occupancy} pax</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:15,fontWeight:900,color:C.gold}}>{inr(rt.base_tariff)}</div>
                    <div style={{fontSize:10,color:C.text3}}>per night</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Active Pricing Rules */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:14}}>📋 Active Pricing Rules</div>
              {(context.pricing_rules||[]).map((rule,i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.surface2,borderRadius:8,marginBottom:8}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{rule.rule_name}</div>
                    <div style={{fontSize:11,color:C.text3,textTransform:'capitalize'}}>{rule.rule_type}</div>
                  </div>
                  <div style={{fontSize:14,fontWeight:800,color:parseFloat(rule.adjustment_value)>0?C.amber:C.accent}}>
                    {parseFloat(rule.adjustment_value)>0?'+':''}{rule.adjustment_value}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Revenue by booking type */}
          {(context.revenue_by_type||[]).length > 0 && (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:14}}>📊 Revenue by Booking Type (Last 30 Days)</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10}}>
                {context.revenue_by_type.map((rt,i)=>(
                  <div key={i} style={{padding:'12px 16px',background:C.surface2,borderRadius:10}}>
                    <div style={{fontSize:12,color:C.text3,marginBottom:4}}>{rt.booking_type?.replace(/[^\w\s]/g,'').trim()}</div>
                    <div style={{fontSize:18,fontWeight:800,color:C.gold}}>{inr(rt.revenue)}</div>
                    <div style={{fontSize:11,color:C.text2}}>{rt.count} bookings · Avg {inr(rt.avg_value)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!recommendations && (
            <div style={{marginTop:16,padding:'20px',background:`${C.gold}11`,border:`1px solid ${C.gold}44`,borderRadius:12,textAlign:'center'}}>
              <div style={{fontSize:15,fontWeight:700,color:C.gold,marginBottom:8}}>🧠 Run AI Revenue Analysis</div>
              <div style={{fontSize:13,color:C.text2,marginBottom:14}}>Get AI-powered pricing recommendations, rate optimization, and revenue forecast</div>
              <button onClick={analyze} disabled={analyzing}
                style={{padding:'10px 24px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                {analyzing?'⏳ Analyzing...':'Run Analysis Now'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* RECOMMENDATIONS TAB */}
      {activeTab==='recommendations' && (
        <div>
          {!recommendations ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:36,marginBottom:12}}>🧠</div>
              <div style={{fontSize:15,fontWeight:600,color:C.text2,marginBottom:8}}>No analysis yet</div>
              <button onClick={analyze} style={{padding:'10px 24px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                Run Revenue Analysis
              </button>
            </div>
          ) : (
            <div>
              {/* Health Score */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:20}}>
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,gridColumn:'span 1'}}>
                  <div style={{fontSize:11,color:C.text3,textTransform:'uppercase',marginBottom:8}}>Revenue Health Score</div>
                  <div style={{fontSize:48,fontWeight:900,color:healthColor(recommendations.health_score||0)}}>{recommendations.health_score||0}</div>
                  <div style={{fontSize:14,fontWeight:700,color:healthColor(recommendations.health_score||0)}}>{recommendations.overall_health}</div>
                </div>
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,gridColumn:'span 2'}}>
                  <div style={{fontSize:11,color:C.text3,textTransform:'uppercase',marginBottom:8}}>AI Summary</div>
                  <div style={{fontSize:14,color:C.text,lineHeight:1.7}}>{recommendations.summary}</div>
                </div>
              </div>

              {/* Recommendations */}
              <div style={{display:'grid',gap:10}}>
                {(recommendations.recommendations||[]).map((rec,i)=>{
                  const p = PRIORITY_CONFIG[rec.priority]||PRIORITY_CONFIG.Medium;
                  return (
                    <div key={i} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18,borderLeft:`4px solid ${p.color}`}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <span style={{fontSize:16}}>{p.icon}</span>
                          <span style={{padding:'2px 8px',borderRadius:99,fontSize:11,fontWeight:700,background:p.bg,color:p.color}}>{rec.priority} Priority</span>
                          <span style={{padding:'2px 8px',borderRadius:99,fontSize:11,fontWeight:700,background:C.surface2,color:C.text2}}>{CATEGORY_ICONS[rec.category]} {rec.category}</span>
                        </div>
                        <span style={{fontSize:11,color:C.text3}}>{rec.timeframe}</span>
                      </div>
                      <div style={{fontSize:15,fontWeight:800,color:C.text,marginBottom:6}}>{rec.title}</div>
                      <div style={{fontSize:13,color:C.text2,marginBottom:8,lineHeight:1.6}}>{rec.action}</div>
                      <div style={{fontSize:12,color:C.gold,fontWeight:600}}>📈 Expected Impact: {rec.expected_impact}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* RATE ADVISOR TAB */}
      {activeTab==='rates' && (
        <div>
          {!recommendations?.rate_recommendations ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:36,marginBottom:12}}>💰</div>
              <div style={{fontSize:15,fontWeight:600,color:C.text2,marginBottom:8}}>Run analysis first to get rate recommendations</div>
              <button onClick={analyze} style={{padding:'10px 24px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                Run Revenue Analysis
              </button>
            </div>
          ) : (
            <div>
              <div style={{padding:'12px 16px',background:`${C.amber}11`,border:`1px solid ${C.amber}33`,borderRadius:10,marginBottom:16,fontSize:12,color:C.amber}}>
                ⚠️ These are AI recommendations only. All rate changes require manual approval in the Pricing Manager.
              </div>
              <div style={{display:'grid',gap:12}}>
                {recommendations.rate_recommendations.map((rate,i)=>{
                  const change = rate.recommended_rate - rate.current_rate;
                  const changePct = Math.round(change/rate.current_rate*100);
                  return (
                    <div key={i} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                        <div style={{fontSize:16,fontWeight:800,color:C.text}}>{rate.room_type}</div>
                        <span style={{padding:'3px 10px',borderRadius:99,fontSize:12,fontWeight:700,
                          background:changePct>0?C.amberBg:C.greenBg,color:changePct>0?C.amber:C.green}}>
                          {changePct>0?'▲':'▼'} {Math.abs(changePct)}% change suggested
                        </span>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12}}>
                        <div style={{textAlign:'center',padding:'14px',background:C.surface2,borderRadius:8}}>
                          <div style={{fontSize:11,color:C.text3,marginBottom:4}}>Current Rate</div>
                          <div style={{fontSize:20,fontWeight:800,color:C.text2}}>{inr(rate.current_rate)}</div>
                        </div>
                        <div style={{textAlign:'center',padding:'14px',background:C.surface2,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',fontSize:24}}>→</div>
                        <div style={{textAlign:'center',padding:'14px',background:`${C.gold}22`,borderRadius:8,border:`1px solid ${C.gold}44`}}>
                          <div style={{fontSize:11,color:C.gold,marginBottom:4}}>AI Recommended</div>
                          <div style={{fontSize:20,fontWeight:800,color:C.gold}}>{inr(rate.recommended_rate)}</div>
                        </div>
                      </div>
                      <div style={{fontSize:12,color:C.text2,marginBottom:6}}><strong style={{color:C.text}}>Reason:</strong> {rate.reason}</div>
                      <div style={{fontSize:11,color:C.text3}}>Valid for: {rate.valid_for}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* FORECAST TAB */}
      {activeTab==='forecast' && (
        <div>
          {!recommendations?.forecast ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:36,marginBottom:12}}>📈</div>
              <div style={{fontSize:15,fontWeight:600,color:C.text2,marginBottom:8}}>Run analysis to see revenue forecast</div>
              <button onClick={analyze} style={{padding:'10px 24px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                Run Revenue Analysis
              </button>
            </div>
          ) : (
            <div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
                {[
                  {label:'Next 7 Days Revenue',value:inr(recommendations.forecast.next_7_days_revenue),color:C.blue,icon:'📅'},
                  {label:'Next 30 Days Revenue',value:inr(recommendations.forecast.next_30_days_revenue),color:C.gold,icon:'📆'},
                  {label:'Occupancy Forecast',value:recommendations.forecast.occupancy_forecast,color:C.green,icon:'🏨'},
                  {label:'Forecast Confidence',value:recommendations.forecast.confidence,color:C.amber,icon:'🎯'},
                ].map((k,i)=>(
                  <div key={i} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:'18px',borderTop:`3px solid ${k.color}`}}>
                    <div style={{fontSize:24,marginBottom:8}}>{k.icon}</div>
                    <div style={{fontSize:20,fontWeight:900,color:k.color}}>{k.value}</div>
                    <div style={{fontSize:11,color:C.text3,marginTop:4,textTransform:'uppercase'}}>{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Next 7 days bookings */}
              {(context?.next_7_days||[]).length > 0 && (
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
                  <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:14}}>📅 Next 7 Days Bookings</div>
                  {context.next_7_days.map((day,i)=>(
                    <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'10px 14px',background:C.surface2,borderRadius:8,marginBottom:6}}>
                      <div style={{fontSize:13,color:C.text}}>{new Date(day.date).toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'short'})}</div>
                      <div style={{display:'flex',gap:16}}>
                        <span style={{fontSize:13,color:C.text2}}>{day.bookings} bookings</span>
                        <span style={{fontSize:13,fontWeight:700,color:C.gold}}>{inr(day.revenue)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* CHAT TAB */}
      {activeTab==='chat' && (
        <div>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,overflow:'hidden',marginBottom:12}}>
            <div style={{maxHeight:420,overflowY:'auto',padding:20}}>
              {chatMessages.length===0 && (
                <div style={{textAlign:'center',padding:40,color:C.text3}}>
                  <div style={{fontSize:32,marginBottom:10}}>💰</div>
                  <div style={{fontSize:14,color:C.text2,marginBottom:8}}>Ask Revenue AI anything</div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center',marginTop:16}}>
                    {['Should I increase weekend rates?','What is our RevPAR today?','When should we offer discounts?','How to increase occupancy this month?'].map(q=>(
                      <button key={q} onClick={()=>{setChatInput(q);}} style={{padding:'6px 12px',background:C.surface2,border:`1px solid ${C.border}`,borderRadius:8,color:C.text2,fontSize:12,cursor:'pointer'}}>{q}</button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map((m,i)=>(
                <div key={i} style={{marginBottom:14,display:'flex',flexDirection:m.role==='user'?'row-reverse':'row',gap:8}}>
                  <div style={{width:28,height:28,borderRadius:'50%',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,
                    background:m.role==='user'?C.primary:`linear-gradient(135deg,${C.gold},#E8C97A)`}}>
                    {m.role==='user'?'👤':'💰'}
                  </div>
                  <div style={{maxWidth:'80%',padding:'10px 14px',borderRadius:10,background:m.role==='user'?C.primary:C.surface2,border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:13,lineHeight:1.7,color:C.text,whiteSpace:'pre-wrap'}}>{m.content}</div>
                  </div>
                </div>
              ))}
              {chatLoading && <div style={{textAlign:'center',padding:10,color:C.text3,fontSize:13}}>⏳ Revenue AI thinking...</div>}
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <input value={chatInput} onChange={e=>setChatInput(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&askRevenue()}
              placeholder="Ask about pricing, occupancy, revenue optimization..."
              style={{flex:1,padding:'12px 16px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:10,color:C.text,fontSize:13,outline:'none'}} />
            <button onClick={askRevenue} disabled={chatLoading||!chatInput.trim()}
              style={{padding:'12px 20px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer',opacity:chatLoading||!chatInput.trim()?0.5:1}}>
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
